import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ErrorList } from '../../../shared-components/controls/error-prompt/error-list.model';
import { StudentSearchComponent } from '../../../shared-components/student-search/student-search.component';
import { User } from '../../../shared-components/user/user.model';
import { UserService } from '../../../shared-components/user/user.service';
import { LoginService } from '../login.service';
import { LoginSettings } from '../loginSetting.model';

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.css']
})

export class ChangePasswordComponent implements OnInit {
    isDebug = false; // Used for debugging
    currentPassword = '';
    newPassword = '';
    repeatNewPassword = '';
    errorList: ErrorList[] = [];
    LoginSettings: LoginSettings;
    User: User;
    IsChangePasswordUrl = false;
    requiredCurrentPassword = true;

    @ViewChild('currentPasswordRef', { read: ElementRef, static: true }) currentPasswodRef: ElementRef;
    @ViewChild('newPasswordRef', { read: ElementRef, static: true }) newPasswordRef: ElementRef;
    @ViewChild('repeatNewPasswordRef', { read: ElementRef, static: true }) repeatNewPasswordRef: ElementRef;

    constructor(
        private dialogRef: MatDialogRef<ChangePasswordComponent>,
        private loginService: LoginService,
        private userService: UserService,
        private router: Router,
        private dialog: MatDialog) {
    }

    ngOnInit(): void {
        this.User = this.userService.currentUser;
        this.LoginSettings = this.loginService.LoginSettings;
        if (this.isDebug) {
            console.log('User', this.User);
            console.log('LoginSettings', this.LoginSettings);
        }
        if (this.router.url == '/changePassword') {
            this.IsChangePasswordUrl = true;
        }
        if (this.LoginSettings.V10 == false && this.User.UserType == 1) {
            this.requiredCurrentPassword = false;
        }
    }

    passwordValidation(): boolean {
        const mixedLettersNumbersRegEx = /^(?=.*\d)(?=.*[A-Za-z])[0-9a-zA-Z_@./#!$%^&*+-]*$/;
        const containsNumericRegEx = new RegExp('(?=.*[0-9])');
        const containsSpecialRegEx = new RegExp('(?=.*[^a-zA-Z0-9+])');
        const containsUpperRegEx = new RegExp('(?=.*[A-Z])');
        let passesValidation = true;
        // Validate that the newPassword and repeatNewPassword are the same
        if (this.newPassword !== this.repeatNewPassword) {
            if (this.LoginSettings.V10) {
                this.errorList.push({
                    fieldName: undefined,
                    fieldRef: undefined,
                    errorMsg: 'New passwords do not match',
                });
                passesValidation = false;
            }
            else if (this.newPassword.toLowerCase() !== this.repeatNewPassword.toLowerCase() || (this.userService.currentUser.UserType == 1 && this.LoginSettings.Student360StaffPasswordCaseSensitive == true)) {
                this.errorList.push({
                    fieldName: undefined,
                    fieldRef: undefined,
                    errorMsg: 'New passwords do not match',
                });
                passesValidation = false;
            }
        }
        // Validate that all fields have been provided
        if (this.requiredCurrentPassword && (this.currentPassword == undefined || this.currentPassword == '')) {
            this.errorList.push({
                fieldName: 'Current Password',
                fieldRef: this.currentPasswodRef,
                errorMsg: 'Field required',
            });
            passesValidation = false;
        }
        if (this.newPassword == undefined || this.newPassword == '') {
            this.errorList.push({
                fieldName: 'New Password',
                fieldRef: this.newPasswordRef,
                errorMsg: 'Field required',
            });
            passesValidation = false;
        }
        if (this.repeatNewPassword == undefined || this.repeatNewPassword == '') {
            this.errorList.push({
                fieldName: 'Repeat New Password',
                fieldRef: this.repeatNewPasswordRef,
                errorMsg: 'Field required',
            });
            passesValidation = false;
        }
        if (this.LoginSettings.V10 || this.userService.currentUser.UserType == 1) {
            // Minimum password length
            if (this.newPassword.length < this.loginService.LoginSettings.MinimumPasswordLength) {
                this.errorList.push({
                    fieldName: undefined,
                    fieldRef: undefined,
                    errorMsg: `Password must be greater than ${this.loginService.LoginSettings.MinimumPasswordLength} characters.`,
                });
                passesValidation = false;
            }
            // required mixed letters and numbers
            if (this.LoginSettings.PasswordRequiresMixedLettersAndNumbers && !this.newPassword.match(mixedLettersNumbersRegEx)) {
                this.errorList.push({
                    fieldName: undefined,
                    fieldRef: undefined,
                    errorMsg: `New password must contain mixed letters and numbers`,
                });
                passesValidation = false;
            }
            // requires numeric
            if (this.LoginSettings.PasswordRequiresNumeric && !this.newPassword.match(containsNumericRegEx)) {
                this.errorList.push({
                    fieldName: undefined,
                    fieldRef: undefined,
                    errorMsg: `New password must have at least one Numeric character`,
                });
                passesValidation = false;
            }
            // requires special
            if (this.LoginSettings.PasswordRequiresSpecial && !this.newPassword.match(containsSpecialRegEx)) {
                this.errorList.push({
                    fieldName: undefined,
                    fieldRef: undefined,
                    errorMsg: `New password must have at least one Special (! @ # $ ...) character`,
                });
                passesValidation = false;
            }
            // requires upper
            if (this.LoginSettings.PasswordRequiresUpper && !this.newPassword.match(containsUpperRegEx)) {
                this.errorList.push({
                    fieldName: undefined,
                    fieldRef: undefined,
                    errorMsg: `New password must have at least one uppercase character`,
                });
                passesValidation = false;
            }
        }
        // prevent password reuse
        if (this.LoginSettings.PreventPasswordReuse && this.newPassword == this.currentPassword) {
            this.errorList.push({
                fieldName: undefined,
                fieldRef: undefined,
                errorMsg: `New password cannot be the same as the current password`,
            });
            passesValidation = false;
        }
        // Cannot be same as username
        if (this.LoginSettings.PreventEqualPassUserName && this.newPassword.toLowerCase() == this.User.UserName.toLowerCase()) {
            this.errorList.push({
                fieldName: undefined,
                fieldRef: undefined,
                errorMsg: `New password cannot be the same as the user name`,
            });
            passesValidation = false;
        }
        return passesValidation;
    }

    saveClick() {
        // add some validation to make sure that the newPassword and repeatnewPassword match
        const data: string[] = [this.currentPassword, this.newPassword];
        this.errorList = [];
        if (this.passwordValidation()) {
            this.userService.changeUserPassword(this.User.UserType, this.User.ForceChangeExpiredPass, 'Change Password', data).subscribe(response => {
                if (response.body == 'Success') {
                    console.log(response);
                    // This will show at login then re-route appropriately
                    if (this.User.ForceChangePassword || this.User.ForceChangeExpiredPass) {
                        this.User.ForceChangeExpiredPass = false;
                        this.User.ForceChangePassword = false;

                        if (this.User.NewFamilyRegistration) {
                            this.router.navigateByUrl('/onlineRegistration');
                        } else if (this.User.UserType == 1 && this.LoginSettings.ActivateClassroom360) {
                            this.router.navigateByUrl('/classroom360');
                        } else {
                            if (this.User.UserType == 1) {
                                this.dialog.open(StudentSearchComponent,
                                    {
                                        data: {
                                            performRoute: true,
                                            searchAndSelect: false
                                        },
                                        width: '100%',
                                        height: '100%',
                                        maxHeight: '100vh',
                                        maxWidth: '100vw',
                                        disableClose: true
                                    }
                                );
                            }
                            else {
                                this.router.navigateByUrl('/student360');
                            }
                        }
                    }
                    if (this.IsChangePasswordUrl == false) {
                        this.closeClick();
                    }
                } else if (response.body == 'Current password invalid') {
                    this.errorList.push({
                        fieldName: undefined,
                        fieldRef: this.currentPasswodRef,
                        errorMsg: 'Current password invalid',
                    });
                }
            });
        }
    }

    closeClick() {
        // POST: the change password dialog is closed
        if (this.IsChangePasswordUrl == true) {
            this.router.navigateByUrl('/login');
        } else {
            this.dialogRef.close();
        }
    }
}
